import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Settings2 } from 'lucide-react';
import { useTerritoryStore } from '../store/territoryStore';
import type { OpportunityCluster } from '../types';
import {
  AnalyzeStatsBar,
  AnalyzeSettingsModal,
  SaturationMap,
  ClusterTable,
  HireRecommendationPanel,
  ClusterDetailView,
} from '../components/territories';

export default function TerritoryAnalyze() {
  const {
    analyzeData,
    analyzeSettings,
    isLoadingAnalysis,
    analyzeError,
    fetchAnalysis,
    setAnalyzeSettings,
  } = useTerritoryStore();

  const [showSettings, setShowSettings] = useState(false);
  const [drilldownCluster, setDrilldownCluster] = useState<OpportunityCluster | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const handleApplySettings = (newSettings: typeof analyzeSettings) => {
    setAnalyzeSettings(newSettings);
    fetchAnalysis(newSettings);
  };

  const handleClusterDrilldown = (clusterId: string) => {
    const cluster = analyzeData?.clusters.find(c => c.id === clusterId);
    if (!cluster) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setDrilldownCluster(cluster);
      setIsTransitioning(false);
    }, 300);
  };

  const handleBackFromDrilldown = () => {
    setIsTransitioning(true);
    setDrilldownCluster(null);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Cluster detail drill-down view
  if (drilldownCluster) {
    return (
      <div className={`p-6 transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        <ClusterDetailView cluster={drilldownCluster} onBack={handleBackFromDrilldown} />
      </div>
    );
  }

  // National analysis view
  return (
    <div className={`p-6 flex flex-col h-full transition-all duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
      {/* Page header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Link
              to="/territories"
              className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Map
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Territory Analysis</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Opportunity clusters and hiring recommendations</p>
        </div>
        <button
          onClick={() => setShowSettings(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Settings2 className="w-4 h-4" />
          Settings
        </button>
      </div>

      {/* Stats row */}
      <AnalyzeStatsBar
        stats={analyzeData?.nationalStats ?? null}
        clusterCount={analyzeData?.clusters.length ?? 0}
        topScore={analyzeData?.clusters[0]?.opportunityScore ?? 0}
        isLoading={isLoadingAnalysis}
      />

      {/* Error state */}
      {analyzeError && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-700 dark:text-red-400">{analyzeError}</p>
        </div>
      )}

      {/* Map + Table */}
      <div className="flex gap-4 flex-1 min-h-0">
        <div className="flex-[2] min-h-[400px]">
          {isLoadingAnalysis && !analyzeData ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm flex items-center justify-center h-full">
              <div className="text-gray-400 dark:text-gray-500 text-sm">Running analysis...</div>
            </div>
          ) : analyzeData ? (
            <SaturationMap
              stateHeatmap={analyzeData.stateHeatmap}
              clusters={analyzeData.clusters}
              selectedClusterId={null}
              onClusterClick={handleClusterDrilldown}
            />
          ) : null}
        </div>

        <div className="flex-1 min-h-[400px]">
          <ClusterTable
            clusters={analyzeData?.clusters ?? []}
            selectedClusterId={null}
            onSelectCluster={handleClusterDrilldown}
            onViewTerritory={handleClusterDrilldown}
          />
        </div>
      </div>

      {/* Hire Recommendations */}
      {analyzeData && <HireRecommendationPanel clusters={analyzeData.clusters} />}

      {/* Settings Modal */}
      {showSettings && (
        <AnalyzeSettingsModal
          settings={analyzeSettings}
          onApply={handleApplySettings}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
